import { stringify } from "querystring";
import CodeBlock from "../../common/code_block";
import HeaderWithTag from "../../common/header_with_tag";
import { IGetTokenRequest } from "./types";
import TryGetToken from "./try";
import ErrorCodesLink from "../../common/ErrorCodesLink";
import { useContext } from "react";
import { AuthContext } from "../../auth/authContext";
import { HOST_API_URL } from "../../config";


function GetToken() {

  const { id } = useContext(AuthContext);

  return (
    <div className="page">
      <div className="app-content main-content mt-0">
        <div className="side-app">
          <div className="main-container container-fluid">
            <div className="page-header">
              <div>
                <h1 className="page-title">Get Token</h1>
              </div>
              <div className="ms-auto pageheader-btn">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="javascript:void(0);">Docs</a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Get Token
                  </li>
                </ol>
              </div>
            </div>

            <div className="card overflow-hidden">
              <div className="row">
                <div className="col-md-7" style={{ position: "relative" }}>
                  <div className="card-body border-end p-0">
                    <div className="p-5">
                      <HeaderWithTag
                        headerText="About Authentication"
                        headerTag="description"
                      />
                      <p className="text-12">
                        <span className="badge badge-sm bg-blue text-white  me-2">POST</span>
                        {HOST_API_URL}/accounts/getToken
                      </p>
                      <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                        API authentication uses HTTP Bearer Authentication, a secure token-based method.
                        After a successful login, the API issues a token to the partner, which must be included
                        in the <strong>Authorization</strong> header for all subsequent requests. Tokens are valid for
                        10 minutes; the exact expiry is returned in <code>data.tokenExpiry</code> and{" "}
                        <code>data.tokenValidity</code>, so read those rather than hard-coding the lifetime.
                      </p>
                      <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                        The token is generated by sending a <strong>POST</strong> request to the API with the partner's
                        <code>username</code> and <code>password</code>. The API responds with a token that can be used for subsequent
                        requests.
                      </p>
                      <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                        Include the token in the <strong>Authorization</strong> header of all API requests as follows:<br />
                        <code>Authorization: Bearer &lt;token&gt;</code>
                      </p>
                      <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                        A request made with a missing, invalid or expired token is answered with{" "}
                        <strong>401 Unauthorized</strong> and an empty body (no envelope, no{" "}
                        <code>errorCode</code>). Request a new token and retry.
                      </p>
                      <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                        All API requests must use <strong>HTTPS</strong>. Plain-HTTP requests are redirected and will
                        not be processed as sent.
                      </p>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Request"
                          headerTag="sampleRequest"
                        />
                        <div className="table-responsive">
                          <table className="table text-nowrap text-md-nowrap table-bordered">
                            <thead>
                              <tr>
                                <th>Parameter</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                                <th>Min</th>
                                <th>Max</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>username</td>
                                <td>string</td>
                                <td>
                                  Your API username. Must not be empty or whitespace.
                                </td>
                                <td>Yes</td>
                                <td></td>
                                <td></td>
                              </tr>
                              <tr>
                                <td>password</td>
                                <td>string</td>
                                <td>
                                  Your API password. Must not be empty or whitespace.
                                </td>
                                <td>Yes</td>
                                <td></td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="my-5">
                        <CodeBlock
                          code={JSON.stringify({ username: id, password: "*****************" }, null, 2)}
                          codeBlackText="Sample Request"
                          codeLanguage="json"
                        />
                      </div>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Sample Response"
                          headerTag="sampleResponse"
                        />
                        <div className="table-responsive">
                          <table className="table text-nowrap text-md-nowrap table-bordered">
                            <thead>
                              <tr>
                                <th>Parameter</th>
                                <th>Type</th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>statusCode</td>
                                <td>integer</td>
                                <td>200 on success, otherwise the failure status</td>
                              </tr>
                              <tr>
                                <td>message</td>
                                <td>string</td>
                                <td>"Success", or the reason for the failure</td>
                              </tr>
                              <tr>
                                <td>errorCode</td>
                                <td>string</td>
                                <td>null on success and on the 404; DPI-1000 / DPI-1006 on request-validation failures</td>
                              </tr>
                              <tr>
                                <td>data.token</td>
                                <td>string</td>
                                <td>The authentication token to use in requests</td>
                              </tr>
                              <tr>
                                <td>data.tokenExpiry</td>
                                <td>string</td>
                                <td>UTC timestamp (ISO 8601) at which the token stops being accepted</td>
                              </tr>
                              <tr>
                                <td>data.tokenValidity</td>
                                <td>integer</td>
                                <td>Token lifetime in seconds</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="my-5">
                        <CodeBlock
                          code={JSON.stringify(
                            {
                              "statusCode": 200,
                              "message": "Success",
                              "errorCode": null,
                              "data": {
                                "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
                                "tokenExpiry": "2026-10-16T08:54:47.4537563Z",
                                "tokenValidity": 600
                              }
                            },
                            null,
                            2
                          )}
                          codeBlackText="Sample Response"
                          codeLanguage="json"
                        />
                      </div>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Failure Responses"
                          headerTag="failures"
                        />
                        <div className="alert alert-info text-14">
                          <strong>Get Token</strong> returns <code>errorCode</code> null on
                          its 404 (user not found or inactive). Only its request-validation
                          failures carry a code: DPI-1000 for a missing username or
                          password, DPI-1006 for a malformed body. Branch on the HTTP status
                          and read <code>message</code>.
                        </div>
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th className="text-nowrap">Status</th>
                                <th>Message</th>
                                <th>Meaning</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="text-nowrap">400</td>
                                <td>Username is required / Password is required</td>
                                <td>
                                  A required field is missing or empty. errorCode DPI-1000.
                                </td>
                              </tr>
                              <tr>
                                <td className="text-nowrap">400</td>
                                <td>The JSON value could not be converted …</td>
                                <td>
                                  The body is not valid JSON for the request. errorCode
                                  DPI-1006.
                                </td>
                              </tr>
                              <tr>
                                <td className="text-nowrap">404</td>
                                <td>User not found or inactive</td>
                                <td>
                                  The credentials did not match an active user; the lookup
                                  is also scoped to the calling IP address. errorCode null.
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <ErrorCodesLink noErrorCode />
                    </div>
                  </div>
                </div>
                <div className="col-md-5 pt-5">
                  <h5 className="card-title text-primary">
                    Code Samples
                  </h5>

                  <TryGetToken
                    codeBlackTitle="Request Example"
                    getTokenObject={{ username: id ?? "", password: "*****************" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GetToken;
